<template>
  <v-row align="center"
         no-gutters
         class="ma-0 pages-container">
    <template v-if="!showPreview">
      <v-col v-for="(page, index) in mappedPages" :key="page.id" cols="auto">
        <reg-document-page-item :page="page"
                                :index="index"
                                :show-delete="showDelete"
                                @delete="onDeletePage"
                                @click="onOpenPage" />
      </v-col>
      <slot name="action" />
    </template>
    <v-row v-else
           align="center"
           class="ma-2 text-no-wrap">
      <draggable :options="dragOptions"
                 :value="mappedPages"
                 element="v-col"
                 row
                 shrink
                 @change="onDragPageChange">
        <reg-document-page-item-preview v-for="(page, index) in mappedPages"
                                        :key="page.id"
                                        :page="page"
                                        :index="index"
                                        :show-delete="showDelete"
                                        @rotate="onRotatePage"
                                        @delete="onDeletePage"
                                        @click="onOpenPage" />
      </draggable>
      <slot name="action" />
    </v-row>
  </v-row>
</template>

<script>
import DocumentPageItem from '~shared/DocumentPageItem.vue'
import DocumentPageItemPreview from '~shared/DocumentPageItemPreview.vue'
import DownloadMixin from '~mixins/DownloadMixin'
import CONFIG from '~config/env'
import Draggable from 'vuedraggable'
import _ from 'lodash'

export default {
  name: 'RegDocumentPages',
  components: {
    'reg-document-page-item': DocumentPageItem,
    'reg-document-page-item-preview': DocumentPageItemPreview,
    draggable: Draggable
  },
  mixins: [DownloadMixin],
  props: {
    documentId: {
      type: String,
      default: ''
    },
    documentName: {
      type: String,
      default: ''
    },
    documentCaptionHTML: {
      type: String,
      default: ''
    },
    pages: {
      type: Array,
      default: () => []
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    showPreview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      urls: []
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 100,
        delay: this.$vuetify.breakpoint.xsOnly ? 200 : 0,
        group: 'taskColumns',
        ghostClass: 'ghost',
        handle: '.page-preview'
      }
    },
    mappedPages() {
      return this.pages
        .filter((page) => page.deleted !== true)
        .map((page, index) => {
          const fileExtension = page?.url?.split('.').pop() || ''
          const customName = this.documentName + ' - Página ' + (page?.page || '') + '.' + fileExtension
          const key = page?.url?.replace(CONFIG.AWS.S3_PUBLIC_URL + '/', '').replace(CONFIG.AWS.S3_PRIVATE_URL + '/', '') || ''
          const signedKey = this.urls.find(url => url.key === key)?.signedKey

          return {
            ...page,
            index,
            key,
            signedKey,
            customName
          }
        })
    }
  },
  watch: {
    showPreview: {
      immediate: true,
      handler: 'fetchSignedUrls'
    },
    documentId: {
      immediate: true,
      handler: 'fetchSignedUrls'
    }
  },
  methods: {
    async fetchSignedUrls() {
      if (this.showPreview) {
        this.getPhotoSignedUrls()
      } else {
        this.urls = []
      }
    },
    onRotatePage(pageRotation) {
      this.$emit('rotate', pageRotation)
    },
    onDeletePage(page) {
      this.$emit('delete', page)
    },
    async getPhotoSignedUrls() {
      this.urls = await this.getMultipleSignedS3Url(this.mappedPages)
    },
    async onOpenPage(pageToOpen) {
      if (String(pageToOpen.type).startsWith('image/')) {
        if (!this.urls.length) {
          await this.getPhotoSignedUrls()
        }
        const selectedPageIndex = pageToOpen.index

        const photos = this.mappedPages.map((page, index) => {
          return {
            src: page.signedKey,
            url: page.signedKey,
            w: page.width || 1000,
            h: page.height || 1000,
            rotation: page.rotation || 0,
            title: this.documentCaptionHTML
          }
        })

        this.$photoGallery.open(selectedPageIndex, photos, {
          fullscreenEl: false,
          shareButtons: [
            {
              id: 'download',
              label: this.$t('download'),
              url: '{{raw_image_url}}',
              download: true
            }
          ]
        })
      } else {
        const fileExtension = pageToOpen.url.split('.').pop()
        const customName = this.documentName + ' - Página ' + pageToOpen.page + '.' + fileExtension
        this.downloadFileFromS3(pageToOpen, customName)
      }
    },
    onDragPageChange({ moved }) {
      if (moved) {
        const newPages = _.cloneDeep(this.pages.filter((page) => page.deleted !== true))
        const removed = newPages.splice(moved.oldIndex, 1)[0]
        newPages.splice(moved.newIndex, 0, removed)
        newPages.push(...this.pages.filter((page) => page.deleted === true))
        this.$emit('drag', newPages)
      }
    }
  }
}
</script>
<style lang="scss">
  .pages-container {
    min-height: 84px
  }
</style>
